import { bool, func } from "prop-types";
import React, { useEffect, useState } from "react";



import styled from "styled-components/macro";

import { useSignups } from "Signups/state";
import { useBackgroundColor } from "Signups/stateHooks";

import TempColorPicker from "UI/TempColorPicker/TempColorPicker";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

import { ensureLeadingHex } from "Utilities/Colors/colorUtils";

import { useEditSignupBackgroundColor } from "./api/queries";

export const BackgroundEditor = (props) => {
  const { showColorPicker, setShowColorPicker } = props;
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, [setIsMounted]);

  const [state] = useSignups();
  const signupID = state?.currentSignupList?.ID;

  // Current color in state
  const {
    backgroundColor: selectedColor,
    setBackgroundColorInfo,
    backgroundColorScheme,
  } = useBackgroundColor();

  // actively editing color
  const [color, setColor] = useState(selectedColor);
  const [gradient, setGradient] = useState(backgroundColorScheme);

  const backgroundColor = ensureLeadingHex(color).toLowerCase();

  const {
    mutate,
    isLoading,
    isError: hasError,
  } = useEditSignupBackgroundColor({
    signupID,
    backgroundColor,
    onSuccess: (data) => {
      setBackgroundColorInfo(data);
      setShowColorPicker(false);
    },
  });

  const saveBackgroundColor = () => {
    const gradientWithoutHashes = gradient.map((color) =>
      color.replace("#", "")
    );
    mutate({ color: backgroundColor, colorScheme: gradientWithoutHashes });
  };

  const toggleShow = () => {
    setShowColorPicker((current) => !current);
  };

  const error = hasError
    ? "Hmm. Something went wrong saving your background color. Please try again"
    : null;

  useEffect(() => {
    if (!showColorPicker && selectedColor !== backgroundColor && isMounted)
      setColor(selectedColor);
  }, [showColorPicker, selectedColor, backgroundColor, setColor, isMounted]);

  // useEffect(
  //   function hideOnSuccessfulSave() {
  //     if (selectedColor === backgroundColor) setShowColorPicker(false);
  //   },
  //   [selectedColor, backgroundColor, setShowColorPicker]
  // );

  if (!backgroundColor) return null;

  return (
    <Stack {...props}>
      <Button data-cy="Signup_Background_Button" onClick={toggleShow}>
        <Content>
          <FnrHorizontalGap gap={4}>
            <PaintBrushIcon className="far fa-paint-brush" />
            <Text>Background Color</Text>
          </FnrHorizontalGap>

          <div onClick={(e) => e.stopPropagation()}>
            <TempColorPicker
              selectedColor={selectedColor}
              color={backgroundColor}
              setColor={setColor}
              save={saveBackgroundColor}
              disabled={isLoading}
              error={error}
              show={showColorPicker}
              setShow={setShowColorPicker}
              saving={isLoading}
              ariaLabel="Background Color Picker"
              popoverColor="white"
              showGradientSelector={true}
              onSelectGradient={setGradient}
              initialGradient={backgroundColorScheme}
            />
          </div>
        </Content>
      </Button>
    </Stack>
  );
};

BackgroundEditor.propTypes = {
  showColorPicker: bool.isRequired,
  setShowColorPicker: func.isRequired,
};

const Stack = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  position: relative;
`;

const PaintBrushIcon = styled.div`
  font-size: 22px;
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  margin-left: -3px;
  flex-shrink: 0;
  margin-right: 9px;
`;

const Button = styled.div`
  width: 100%;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: ${(props) => {
    if (typeof props.padding === "number")
      return props.padding ? `${props.padding}px` : "10px";
    return props.padding || "10px";
  }};
  border-radius: 8px;
  ${(props) => props.pointer && `cursor: pointer;`}
  background-color: hsl(0 0% 100% / 12%);
  box-shadow: rgba(0, 0, 0, 0.2) 0 1px 6px;
  cursor: pointer;
  transition: background 200ms;

  &:hover {
    background-color: hsl(0 0% 100% / 0.25);
  }

  &:focus-within {
    outline: 2px solid hsl(0 0% 100% / 1);
    outline-offset: -2px;
    background-color: hsl(0 0% 100% / 0.25);
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-width: 200px;
  padding: 0 4px;
  transition: 0.2s;

  ${Button}:hover & {
    transform: scale(1.005);
  }
  ${Button}:active & {
    transform: scale(0.98);
  }
`;

const Text = styled.span`
  transition: 0.2s;
`;